/**
 * Presentación de precios de cliente (REQ-MINT-012/013).
 *
 * El **cálculo del precio se realiza siempre en el backend** (`client_price`
 * de `/models` y del histórico): la UI no compone precios, no recibe coste,
 * margen, modo ni precios explícitos. Este módulo solo normaliza las cifras ya
 * calculadas y las formatea.
 */

import type {
  ClientPrice,
  ModelListResponse,
  ModelPricingRow,
  PriceComponent,
} from '../types';

export const PRICE_COMPONENTS: PriceComponent[] = [
  'input',
  'output',
  'cache_read',
  'cache_write',
];

/** Precio cliente (USD/1M) de un componente; `null` si no hay dato. */
export function clientPriceComponent(
  price: ClientPrice | null | undefined,
  component: PriceComponent,
): number | null {
  if (!price || price.state !== 'available') return null;
  const value = price.components?.[component]?.usd_per_million;
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Compone las filas del listado con el precio ya calculado en el backend.
 * Sin permiso de precio (`price_visibility="omitted"`) ⇒ estado `forbidden`;
 * sin precio publicado ⇒ `no_data`. Nunca se estima.
 */
export function composePricingRows(models: ModelListResponse): ModelPricingRow[] {
  return models.items.map((offering) => {
    const price = offering.client_price ?? null;
    const applicability = offering.price_applicability ?? 'token';
    // Si el backend publica precio cliente, se muestra (aunque la familia sea
    // no-token); «No aplica» queda para modelos no-token sin precio.
    const priceState =
      models.price_visibility !== 'included'
        ? 'forbidden'
        : price?.state === 'available'
          ? 'available'
          : applicability === 'not_applicable'
            ? 'not_applicable'
            : 'no_data';
    return {
      key: offering.offering.sheet_id,
      modelRef: offering.offering.model_ref ?? null,
      displayName: offering.offering.provider_model_id,
      provider: offering.offering.provider,
      region: offering.offering.region,
      intelState: offering.offering.state,
      lifecycleStatus: offering.lifecycle_status ?? null,
      capabilities: offering.capabilities ?? {},
      priceState,
      price,
    };
  });
}

// ---------------------------------------------------------------------------
// Formato
// ---------------------------------------------------------------------------

const USD_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
});

/** Importe USD (`null` → '—'). */
export function formatUsd(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return USD_FORMATTER.format(value);
}

/**
 * Precio unitario (USD/1M) con precisión adaptativa: los componentes de caché
 * pueden ser muy pequeños y no deben redondearse a cero. ≥ 1 ⇒ 2 decimales;
 * < 1 ⇒ hasta 4 decimales (mínimo 2).
 */
export function formatPriceUsd(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: value < 1 ? 4 : 2,
  });
  return formatter.format(value);
}

/** Porcentaje compacto (`null` → '—'; con signo si positivo). */
export function formatPct(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const rounded = Math.round(value * 10) / 10;
  const text = `${rounded.toLocaleString('en-US', { maximumFractionDigits: 1 })} %`;
  return rounded > 0 ? `+${text}` : text;
}

/** Fecha local corta (`null`/inválida → '—'). */
export function formatDate(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString();
}
